import * as React from 'react';
import { memo, Fragment } from 'react';
import styled from 'styled-components';

export interface ICheatSheetRow {
  /** Key caps rendered left-to-right (e.g. `['g', 'd']`, `['⌘', 'K']`). */
  keys: string[];
  label: string;
}

export interface ICheatSheetSection {
  key: string;
  title: string;
  /** Optional one-line explainer under the section title. */
  hint?: string;
  rows: ICheatSheetRow[];
  /** Shown when the section has no rows (e.g. no page-scoped keys). */
  emptyText?: string;
}

interface IShortcutsCheatSheetProps {
  sections: ICheatSheetSection[];
}

/**
 * Read-only shortcuts reference (`?`), rendered inside the palette's Card so the
 * whole feature keeps ONE overlay system. Purely presentational — the container
 * assembles the (already role-gated) sections.
 */
export const ShortcutsCheatSheet = memo<IShortcutsCheatSheetProps>(
  ({ sections }) => (
    <>
      {sections.map((section) => (
        <Section key={section.key}>
          <SectionHead>
            <SectionTitle>{section.title}</SectionTitle>
            {section.hint ? <SectionHint>{section.hint}</SectionHint> : null}
          </SectionHead>

          {section.rows.length ? (
            section.rows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Row key={`${section.key}-${index}`}>
                <Label>{row.label}</Label>
                <Keys>
                  {row.keys.map((cap, capIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Fragment key={capIndex}>
                      {capIndex > 0 ? <Then>+</Then> : null}
                      <Kbd>{cap}</Kbd>
                    </Fragment>
                  ))}
                </Keys>
              </Row>
            ))
          ) : (
            <Empty>{section.emptyText}</Empty>
          )}
        </Section>
      ))}
    </>
  ),
);

ShortcutsCheatSheet.displayName = 'ShortcutsCheatSheet';

const Section = styled.div`
  padding: 6px 4px 10px;

  & + & {
    border-top: 1px solid var(--k-border-ebeff2);
  }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 6px 8px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-616e78);
`;

const SectionHint = styled.span`
  font-size: 12px;
  color: var(--k-fg-c6ccd0);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 7px 8px;
  border-radius: 8px;
`;

const Label = styled.span`
  font-size: 14px;
  color: var(--k-fg-263446);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Keys = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const Then = styled.span`
  font-size: 11px;
  color: var(--k-fg-c6ccd0);
`;

const Kbd = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border: 1px solid var(--k-border-dfe6f1);
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-263446);
  background: var(--k-bg-fafbfd);
`;

const Empty = styled.div`
  padding: 8px;
  font-size: 13px;
  color: var(--k-fg-616e78);
`;
